import { Milestone, Achievement, UnlockedInsight, UserProgress, AITier, JournalEntry, Pattern, Decision } from '../types';

// ========================================
// MILESTONE DEFINITIONS
// ========================================

export const MILESTONE_DEFINITIONS: Omit<Milestone, 'currentProgress' | 'isUnlocked' | 'unlockedAt'>[] = [
  // Entries milestones
  {
    id: 'entries_5',
    type: 'entries_tracked',
    title: '5 Momenti Tracciati',
    description: 'Hai iniziato il tuo viaggio di consapevolezza',
    threshold: 5,
    reward: {
      type: 'insight_unlock',
      value: 'people_network'
    }
  },
  {
    id: 'entries_10',
    type: 'entries_tracked',
    title: '10 Momenti Tracciati',
    description: 'Sto iniziando a conoscerti meglio',
    threshold: 10,
    reward: {
      type: 'insight_unlock',
      value: 'emotional_pulse'
    }
  },
  {
    id: 'entries_20',
    type: 'entries_tracked',
    title: '20 Momenti Tracciati',
    description: 'La tua storia prende forma',
    threshold: 20,
    reward: {
      type: 'insight_unlock',
      value: 'temporal_patterns'
    }
  },
  {
    id: 'entries_50',
    type: 'entries_tracked',
    title: '50 Momenti Tracciati',
    description: 'Possiamo vedere pattern significativi',
    threshold: 50,
    reward: {
      type: 'ai_upgrade',
      value: 'companion'
    }
  },
  {
    id: 'entries_100',
    type: 'entries_tracked',
    title: '100 Momenti Tracciati',
    description: 'Ora ti conosco profondamente',
    threshold: 100,
    reward: {
      type: 'insight_unlock',
      value: 'life_themes'
    }
  },
  {
    id: 'entries_200',
    type: 'entries_tracked',
    title: '200 Momenti Tracciati',
    description: 'Sei diventato un maestro della consapevolezza',
    threshold: 200,
    reward: {
      type: 'ai_upgrade',
      value: 'oracle'
    }
  },
  // Pattern milestones
  {
    id: 'patterns_3',
    type: 'patterns_discovered',
    title: '3 Pattern Scoperti',
    description: 'Inizio a vedere i tuoi schemi comportamentali',
    threshold: 3
  },
  {
    id: 'patterns_10',
    type: 'patterns_discovered',
    title: '10 Pattern Scoperti',
    description: 'La tua mappa mentale si definisce',
    threshold: 10,
    reward: {
      type: 'insight_unlock',
      value: 'value_alignment'
    }
  },
  // Decision milestones
  {
    id: 'decisions_1',
    type: 'decisions_made',
    title: 'Prima Decisione Mappata',
    description: 'Hai iniziato a decidere con consapevolezza',
    threshold: 1
  },
  {
    id: 'decisions_5',
    type: 'decisions_made',
    title: '5 Decisioni Mappate',
    description: 'Posso aiutarti a imparare dai tuoi pattern decisionali',
    threshold: 5,
    reward: {
      type: 'insight_unlock',
      value: 'decision_learning'
    }
  },
  // Consistency milestones
  {
    id: 'streak_3',
    type: 'alignment_streak',
    title: '3 Giorni di Fila',
    description: 'La costanza è la chiave',
    threshold: 3
  },
  {
    id: 'streak_7',
    type: 'alignment_streak',
    title: '7 Giorni di Fila',
    description: 'Una settimana di consapevolezza',
    threshold: 7
  },
  {
    id: 'streak_30',
    type: 'alignment_streak',
    title: '30 Giorni di Fila',
    description: 'È diventata una vera abitudine',
    threshold: 30,
    reward: {
      type: 'insight_unlock',
      value: 'energy_mapping'
    }
  },
  {
    id: 'days_active_7',
    type: 'days_active',
    title: '7 Giorni Attivi',
    description: 'Una settimana insieme',
    threshold: 7
  },
  {
    id: 'days_active_30',
    type: 'days_active',
    title: '30 Giorni Attivi',
    description: 'Un mese di crescita',
    threshold: 30
  }
];

// ========================================
// ACHIEVEMENT DEFINITIONS
// ========================================

export const ACHIEVEMENT_DEFINITIONS: Omit<Achievement, 'isUnlocked' | 'unlockedAt'>[] = [
  {
    id: 'first_step',
    category: 'tracking',
    title: 'Primo Passo',
    description: 'Hai tracciato il tuo primo momento',
    icon: '🌱',
    rarity: 'common',
    requirement: 'Track 1 moment'
  },
  {
    id: 'voice_pioneer',
    category: 'tracking',
    title: 'Voce Autentica',
    description: 'Hai usato la voce per la prima volta',
    icon: '🎤',
    rarity: 'common',
    requirement: 'Use voice input once'
  },
  {
    id: 'visual_storyteller',
    category: 'tracking',
    title: 'Narratore Visuale',
    description: 'Hai aggiunto una foto a un momento',
    icon: '📸',
    rarity: 'common',
    requirement: 'Add a photo to a moment'
  },
  {
    id: 'deep_reflection',
    category: 'depth',
    title: 'Riflessione Profonda',
    description: 'Hai scritto un momento di oltre 200 parole',
    icon: '📝',
    rarity: 'rare',
    requirement: 'Write 200+ words in one moment'
  },
  {
    id: 'pattern_hunter',
    category: 'insight',
    title: 'Cacciatore di Pattern',
    description: 'Hai scoperto il tuo primo pattern comportamentale',
    icon: '🔍',
    rarity: 'rare',
    requirement: 'Discover first pattern'
  },
  {
    id: 'decision_master',
    category: 'growth',
    title: 'Maestro delle Decisioni',
    description: 'Hai mappato 10 decisioni importanti',
    icon: '🎯',
    rarity: 'epic',
    requirement: 'Map 10 decisions'
  },
  {
    id: 'consistency_champion',
    category: 'consistency',
    title: 'Campione di Costanza',
    description: 'Hai tracciato per 30 giorni di fila',
    icon: '🔥',
    rarity: 'epic',
    requirement: '30 day streak'
  },
  {
    id: 'alignment_seeker',
    category: 'growth',
    title: 'Ricercatore di Allineamento',
    description: 'Hai raggiunto 90%+ di allineamento per una settimana',
    icon: '✨',
    rarity: 'legendary',
    requirement: '7 days with 90%+ alignment'
  },
  {
    id: 'self_aware',
    category: 'insight',
    title: 'Consapevolezza Totale',
    description: 'Hai sbloccato tutti gli insights disponibili',
    icon: '🧠',
    rarity: 'legendary',
    requirement: 'Unlock all insights'
  }
];

// ========================================
// INSIGHT UNLOCK DEFINITIONS
// ========================================

export const INSIGHT_DEFINITIONS: Record<string, Omit<UnlockedInsight, 'unlockedAt' | 'isViewed' | 'requiredMilestone'>> = {
  people_network: {
    id: 'people_network',
    type: 'people_network',
    title: 'Rete di Persone',
    description: 'Vedi chi compare più spesso nella tua vita e come influenza il tuo benessere'
  },
  emotional_pulse: {
    id: 'emotional_pulse',
    type: 'emotional_pulse',
    title: 'Polso Emotivo',
    description: 'Analisi delle tue emozioni negli ultimi 7 e 30 giorni con trend e pattern'
  },
  temporal_patterns: {
    id: 'temporal_patterns',
    type: 'temporal_patterns',
    title: 'Pattern Temporali',
    description: 'Scopri quando sei più produttivo, energico e allineato durante il giorno'
  },
  decision_learning: {
    id: 'decision_learning',
    type: 'decision_learning',
    title: 'Apprendimento Decisionale',
    description: 'Analizza i tuoi pattern decisionali e impara dai successi e fallimenti passati'
  },
  life_themes: {
    id: 'life_themes',
    type: 'life_themes',
    title: 'Temi di Vita',
    description: 'Identificazione dei temi narrativi ricorrenti nella tua storia'
  },
  value_alignment: {
    id: 'value_alignment',
    type: 'value_alignment',
    title: 'Allineamento Valori',
    description: 'Quanto le tue azioni quotidiane riflettono i tuoi valori dichiarati'
  },
  energy_mapping: {
    id: 'energy_mapping',
    type: 'energy_mapping',
    title: 'Mappa Energetica',
    description: 'Traccia le tue fluttuazioni energetiche e identifica cosa ti dà o toglie energia'
  }
};

// ========================================
// PROGRESS CALCULATION
// ========================================

export function calculateUserProgress(
  entries: JournalEntry[],
  patterns: Pattern[],
  decisions: Decision[],
  existingProgress?: UserProgress
): UserProgress {
  const totalEntries = entries.length;
  const totalPatterns = patterns.length;
  const totalDecisions = decisions.length;

  // Calculate days active and streak
  const sortedDates = [...new Set(entries.map(e => {
    const d = new Date(e.timestamp);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }))].sort();

  const daysActive = sortedDates.length;

  // Calculate current streak (consecutive days from today backward)
  let currentStreak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < 365; i++) {
    const checkDate = new Date(today);
    checkDate.setDate(checkDate.getDate() - i);
    const dateStr = `${checkDate.getFullYear()}-${checkDate.getMonth()}-${checkDate.getDate()}`;

    if (sortedDates.includes(dateStr)) {
      currentStreak++;
    } else if (i > 0) {
      // Allow 1 day gap if it's today and user hasn't tracked yet
      break;
    }
  }

  // Calculate longest streak
  let longestStreak = 0;
  let tempStreak = 0;
  let lastDate: Date | null = null;

  sortedDates.forEach(dateStr => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const currentDate = new Date(y, m, d);

    if (lastDate) {
      const dayDiff = Math.floor((currentDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
      if (dayDiff === 1) {
        tempStreak++;
      } else {
        longestStreak = Math.max(longestStreak, tempStreak);
        tempStreak = 1;
      }
    } else {
      tempStreak = 1;
    }
    lastDate = currentDate;
  });
  longestStreak = Math.max(longestStreak, tempStreak);

  // Calculate AI tier based on entries and patterns
  const aiTier = calculateAITier(totalEntries, totalPatterns);

  // Process milestones
  const milestones = MILESTONE_DEFINITIONS.map(def => {
    let progress = 0;

    switch (def.type) {
      case 'entries_tracked':
        progress = totalEntries;
        break;
      case 'patterns_discovered':
        progress = totalPatterns;
        break;
      case 'decisions_made':
        progress = totalDecisions;
        break;
      case 'days_active':
        progress = daysActive;
        break;
      case 'alignment_streak':
        progress = currentStreak;
        break;
      case 'insights_unlocked':
        progress = existingProgress?.unlockedInsights.length || 0;
        break;
    }

    const isUnlocked = progress >= def.threshold;
    const existing = existingProgress?.milestones.find(m => m.id === def.id);

    return {
      ...def,
      currentProgress: progress,
      isUnlocked,
      unlockedAt: existing?.unlockedAt || (isUnlocked && !existing ? Date.now() : undefined)
    };
  }).sort((a, b) => a.threshold - b.threshold);

  // Find newly unlocked milestones
  const newlyUnlockedMilestones = milestones.filter(m =>
    m.isUnlocked &&
    !existingProgress?.milestones.find(em => em.id === m.id && em.isUnlocked)
  );

  // Process unlocked insights
  let unlockedInsights = existingProgress?.unlockedInsights || [];

  newlyUnlockedMilestones.forEach(milestone => {
    if (milestone.reward?.type === 'insight_unlock') {
      const insightId = milestone.reward.value;
      const insightDef = INSIGHT_DEFINITIONS[insightId];

      if (insightDef && !unlockedInsights.find(i => i.id === insightId)) {
        unlockedInsights.push({
          ...insightDef,
          requiredMilestone: milestone.id,
          unlockedAt: Date.now(),
          isViewed: false
        });
      }
    }
  });

  // Process achievements
  const achievements = ACHIEVEMENT_DEFINITIONS.map(def => {
    const existing = existingProgress?.achievements.find(a => a.id === def.id);
    let isUnlocked = existing?.isUnlocked || false;

    // Check unlock conditions
    if (!isUnlocked) {
      switch (def.id) {
        case 'first_step':
          isUnlocked = totalEntries >= 1;
          break;
        case 'voice_pioneer':
          // Would need to track if user used voice - placeholder
          isUnlocked = false;
          break;
        case 'visual_storyteller':
          isUnlocked = entries.some(e => e.imageUrl);
          break;
        case 'deep_reflection':
          isUnlocked = entries.some(e => e.content.length > 1000); // ~200 words
          break;
        case 'pattern_hunter':
          isUnlocked = totalPatterns >= 1;
          break;
        case 'decision_master':
          isUnlocked = totalDecisions >= 10;
          break;
        case 'consistency_champion':
          isUnlocked = currentStreak >= 30;
          break;
        case 'alignment_seeker':
          // Check if user had 7 consecutive days with 90%+ alignment
          const last7Days = entries.slice(0, 7);
          isUnlocked = last7Days.length >= 7 &&
            last7Days.every(e => e.goalAlignment >= 90);
          break;
        case 'self_aware':
          isUnlocked = unlockedInsights.length >= Object.keys(INSIGHT_DEFINITIONS).length;
          break;
      }
    }

    return {
      ...def,
      isUnlocked,
      unlockedAt: existing?.unlockedAt || (isUnlocked && !existing ? Date.now() : undefined)
    };
  });

  // Find next milestone
  const nextMilestone = milestones.find(m => !m.isUnlocked);

  return {
    aiTier,
    totalEntries,
    totalPatterns,
    totalDecisions,
    daysActive,
    currentStreak,
    longestStreak,
    milestones,
    achievements,
    unlockedInsights,
    nextMilestone
  };
}

// ========================================
// AI TIER CALCULATION
// ========================================

export function calculateAITier(entriesCount: number, patternsCount: number): AITier {
  // Oracle: Deep knowledge - 200+ entries OR 50+ entries with 15+ patterns
  if (entriesCount >= 200 || (entriesCount >= 50 && patternsCount >= 15)) {
    return 'oracle';
  }

  // Companion: Good knowledge - 50+ entries OR 20+ entries with 5+ patterns
  if (entriesCount >= 50 || (entriesCount >= 20 && patternsCount >= 5)) {
    return 'companion';
  }

  // Learner: Starting to know - 10-49 entries
  if (entriesCount >= 10) {
    return 'learner';
  }

  // Explorer: Very limited knowledge - <10 entries
  return 'explorer';
}

// ========================================
// GET AI TIER DESCRIPTION
// ========================================

export function getAITierDescription(tier: AITier): { title: string; description: string; color: string } {
  const descriptions = {
    explorer: {
      title: 'Esploratore',
      description: 'Sto iniziando a conoscerti. Più condividi, più posso aiutarti.',
      color: 'text-blue-600'
    },
    learner: {
      title: 'Apprendista',
      description: 'Inizio a vedere i tuoi pattern. Continua a tracciare!',
      color: 'text-indigo-600'
    },
    companion: {
      title: 'Compagno',
      description: 'Ti conosco bene. Posso fare connessioni profonde.',
      color: 'text-purple-600'
    },
    oracle: {
      title: 'Oracolo',
      description: 'Ho una conoscenza profonda di te. Posso vedere il quadro completo.',
      color: 'text-amber-600'
    }
  };

  return descriptions[tier];
}
